/* eslint react/prop-types: 0, react/display-name: 0 */

import React from 'react';
import { createFragmentContainer, graphql } from 'react-relay';
import PropTypes from 'prop-types';
import get from 'lodash.get';
import CartPriceTable from './priceTable/CartPriceTable';
import { CurrencyProvider } from 'dibs-buyer-layout/exports/CurrencyProvider';

function getPriceInCurrency(financialAmount, currency) {
    const convertedAmount = (get(financialAmount, 'convertedAmountList') || []).find(
        o => o.currency === currency
    );

    if (!convertedAmount) {
        return null;
    }

    return convertedAmount.amount;
}

function calculateSubtotal({
    showNet,
    privateOffer,
    salePrice,
    retailPrice,
    netPrice,
    shippingPrice,
}) {
    let subtotal;
    let currentPrice;
    if (privateOffer) {
        currentPrice = privateOffer;
    } else if (salePrice) {
        currentPrice = salePrice;
    } else if (showNet && netPrice) {
        // should `&& !retailPrice` be included in this conditions?
        currentPrice = netPrice;
    } else {
        currentPrice = retailPrice;
    }
    if (currentPrice && shippingPrice) {
        subtotal = parseFloat(currentPrice) + parseFloat(shippingPrice);
    } else {
        subtotal = currentPrice;
    }
    return subtotal;
}

function getPrivateOffer({ transaction, currency }) {
    if (transaction) {
        const mostRecentDealerOffer = get(transaction, 'mostRecentDealerOffer');
        let hasActivePrivateOffer = get(transaction, 'isPrivateOffer');
        if (mostRecentDealerOffer) {
            hasActivePrivateOffer = get(transaction, 'mostRecentDealerOffer.isSubmitted');
        }
        if (hasActivePrivateOffer) {
            const mostRecent = get(transaction, 'mostRecentDealerOffer.totalAmount');
            return getPriceInCurrency(mostRecent, currency);
        }
    }
    return null;
}

function CartItemPricing(props) {
    return (
        <CurrencyProvider
            render={({ currency }) => {
                const { item, user } = props;

                const shipmentQuote = get(item, 'shipmentQuotes[0]');
                const transaction = get(item, 'lastTransaction');
                const privateOffer = getPrivateOffer({ transaction, currency });

                const retailPrice = getPriceInCurrency(get(item, 'pricing.retailPrice'), currency);
                const netPrice = getPriceInCurrency(get(item, 'pricing.netPrice'), currency);
                const salePrice = getPriceInCurrency(get(item, 'pricing.salePrice'), currency);

                let shippingPrice = null;
                if (shipmentQuote && shipmentQuote.isComplimentary) {
                    shippingPrice = 0;
                } else if (shipmentQuote) {
                    shippingPrice = getPriceInCurrency(get(shipmentQuote, 'totalAmount'), currency);
                }

                const showSale = get(item, 'cartPriceTableDisplayInfo.pricing.showSale');
                const showNet = get(item, 'cartPriceTableDisplayInfo.pricing.showNet');
                const showRetail = get(item, 'cartPriceTableDisplayInfo.pricing.showRetail');

                const subTotal = calculateSubtotal({
                    showNet,
                    privateOffer,
                    salePrice,
                    retailPrice,
                    netPrice,
                    shippingPrice,
                });

                const priceTableProps = {
                    currency,
                    user,
                    item,

                    privateOffer,
                    retailPrice: !showSale && showRetail ? retailPrice : null,
                    netPrice: showNet ? netPrice : null,
                    salePrice: showSale ? salePrice : null,
                    shippingPrice,
                    subTotal,
                };

                return <CartPriceTable {...priceTableProps} />;
            }}
        />
    );
}

CartItemPricing.propTypes = {
    user: PropTypes.object.isRequired,
    item: PropTypes.object.isRequired,
};

export default createFragmentContainer(CartItemPricing, {
    item: graphql`
        fragment CartItemPricing_item on Item {
            ...CartPriceTable_item
            lastTransaction(buyerId: $userId) {
                isPrivateOffer
                mostRecentDealerOffer {
                    isSubmitted
                    totalAmount {
                        convertedAmountList {
                            currency
                            amount
                        }
                    }
                }
            }
            cartPriceTableDisplayInfo: displayInfo(buyerId: $userId) {
                pricing {
                    showSale
                    showNet
                    showRetail
                }
            }
            pricing {
                retailPrice {
                    convertedAmountList {
                        amount
                        currency
                    }
                }
                salePrice {
                    convertedAmountList {
                        amount
                        currency
                    }
                }
                netPrice {
                    convertedAmountList {
                        amount
                        currency
                    }
                }
            }
            shipmentQuotes {
                isComplimentary
                totalAmount {
                    convertedAmountList {
                        currency
                        amount
                    }
                }
            }
        }
    `,
    user: graphql`
        fragment CartItemPricing_user on User {
            ...CartPriceTable_user
        }
    `,
});
